import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntypedFormControl, Validators, UntypedFormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { Router, RouterModule } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { H3Component } from '@design-system/typography/h3/h3.component';
import { ButtonComponent } from '@design-system/buttons/button/button.component';
import { InputComponent } from '@design-system/forms/input/input.component';
import { AnchorComponent } from '@design-system/buttons/anchor/anchor.component';
import { PasswordInputComponent } from '@design-system/forms/password-input/password-input.component';
import { MicrosoftloginComponent } from './microsoft-login/microsoft-login.component';
import { CheckboxComponent } from '@design-system/forms/checkbox/checkbox.component';
import { UnityCookieService } from '@utils/unity-cookie.service';
import { AuthState } from '../state/models/auth.models';
import { AuthenticationService } from '@auth/authentication.service';
import { environment } from '@env/environment';
import { ActiveAuthenticationStateService } from '@auth/active-authentication.state-service';
import { ActiveSecurityContextStateService } from '@auth/active-security-context.state-service';
import { TestIdDirective } from '@design-system/core/test-id.directive';
import { ThemePreferenceService } from '@design-system/theme/theme-preference.service';
import { IconComponent } from '@design-system/buttons/icon/icon.component';

@UntilDestroy()
@Component({
  selector: 'app-sign-in-flow',
  templateUrl: './sign-in-flow.component.html',
  styleUrl: './sign-in-flow.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    PasswordInputComponent,
    H3Component,
    ButtonComponent,
    InputComponent,
    AnchorComponent,
    MicrosoftloginComponent,
    CheckboxComponent,
    MatError,
    TestIdDirective,
    IconComponent
  ]
})
export class SignInFlowComponent {
  fetching$ = this.authenticationState.inFlight$;

  // sign-in form
  storedEmail = this.cookieService.getCookie('myEmail');
  emailControl = new UntypedFormControl(this.storedEmail, [Validators.required, Validators.email]);
  passwordControl = new UntypedFormControl('', [Validators.required]);
  rememberMeControl = new UntypedFormControl(this.storedEmail !== '');
  signInFormGroup: UntypedFormGroup = new UntypedFormGroup({
    username: this.emailControl,
    password: this.passwordControl,
    rememberMe: this.rememberMeControl
  });

  errorMessage = '';

  constructor(
    public authenticationState: ActiveAuthenticationStateService,
    public theme: ThemePreferenceService,
    private router: Router,
    private authenticationService: AuthenticationService,
    private cookieService: UnityCookieService,
    private activeSecurityContextStateService: ActiveSecurityContextStateService
  ) {}

  async signIn() {
    if (!this.signInFormGroup.valid) return
    await this.authenticationService.userSignIn$(
      this.emailControl.value.toLowerCase(),
      this.passwordControl.value,
      this.rememberMeControl.value
    ).subscribe({
      next: (response: AuthState) => {
        if(response.success === true) {
          this.activeSecurityContextStateService.redirectToBestRoute()
        }
      },
      error: (error) => {
        this.errorMessage = error?.error?.message ?? error?.message ?? 'Sign in failed.';
      }
    });
  }

  showMicrosoftLogin() {
    return environment.renderMsLogin;
  }

  openContactForm() {
    this.router.navigate(['/auth', { outlets: { top: ['contact-us'] } }]).then();
  }

  openForgotPassword() {
    this.router.navigate(['/auth', { outlets: { top: ['forgot-password'] } }]).then();
  }

  toggleDarkMode(): void {
    this.theme.toggle();
  }
}
